<template>
  <div class="h-full flex flex-col overflow-hidden">

    <div class="p-6 pb-0 flex-none max-w-screen-xl mx-auto w-full">
      <BreadCrumbs :navigation="navigation" />
    </div>

    <div class="flex-1 min-h-0 flex gap-4 px-6 pt-4 pb-6 overflow-hidden">

      <div class="flex-none w-72 flex flex-col bg-white shadow-md rounded-lg overflow-hidden">
        <div class="flex-none px-4 pt-4 pb-2 border-b border-gray-100">
          <div class="flex items-center justify-between mb-2">
            <h2 class="text-sm font-semibold text-gray-700">
              <i class="bi bi-inbox mr-1"></i> Pool attività
              <span class="ml-1 text-xs font-normal text-gray-400">({{ filteredPool.length }})</span>
            </h2>
            <button
              @click="reloadPool"
              title="Ricarica pool"
              class="text-gray-400 hover:text-indigo-600 transition-colors"
            >
              <i class="bi bi-arrow-clockwise text-sm"></i>
            </button>
          </div>
          <input
            v-model="poolSearch"
            type="text"
            placeholder="Cerca..."
            class="w-full text-xs border-gray-300 rounded-md"
          />
        </div>

        <div class="flex-1 overflow-y-auto p-2 space-y-2">
          <div
            v-for="act in filteredPool"
            :key="act.id"
            class="rounded-lg border p-3 cursor-pointer hover:shadow-sm transition-shadow"
            :class="selectedPool?.id === act.id ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200 bg-gray-50'"
            @click="selectedPool = (selectedPool?.id === act.id ? null : act)"
          >
            <div class="flex items-start justify-between gap-1 mb-1">
              <span class="text-xs font-medium text-gray-800 leading-tight line-clamp-2">{{ act.title }}</span>
              <span class="flex-none w-2 h-2 rounded-full mt-0.5" :style="{ background: PRIORITY_COLORS[act.priority] || '#6b7280' }"></span>
            </div>
            <div class="text-xs text-gray-500 space-y-0.5">
              <div v-if="act.company_name">{{ act.company_name }}</div>
              <div>Scadenza: {{ act.end_date }}</div>
            </div>
            <div class="mt-2">
              <button
                @click.stop="openAssignDialog(act)"
                class="w-full text-xs px-2 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              >
                <i class="bi bi-person-plus mr-1"></i>Assegna
              </button>
            </div>
          </div>
          <div v-if="filteredPool.length === 0" class="text-center text-xs text-gray-400 py-8 px-2">
            <i class="bi bi-inbox text-2xl block mb-2 opacity-40"></i>
            Nessuna attività nel pool
            <button @click="reloadPool" class="block mx-auto mt-2 text-indigo-500 hover:text-indigo-700 underline">
              Ricarica
            </button>
          </div>
        </div>
      </div>

      <div class="flex-1 min-w-0 flex flex-col bg-white shadow-md rounded-lg overflow-hidden">
        <div class="flex-none flex flex-wrap items-center gap-3 px-4 py-2 border-b border-gray-100">
          <div class="flex items-center gap-2">
            <label class="text-xs font-medium text-gray-600">Priorità:</label>
            <select v-model="filterPriority" @change="applyFilters" class="text-xs border-gray-300 rounded-md">
              <option value="">Tutte</option>
              <option value="bassa">Bassa</option>
              <option value="media">Media</option>
              <option value="alta">Alta</option>
              <option value="critica">Critica</option>
            </select>
          </div>
          <div class="flex items-center gap-2">
            <label class="text-xs font-medium text-gray-600">Stato:</label>
            <select v-model="filterStatus" @change="applyFilters" class="text-xs border-gray-300 rounded-md">
              <option value="">Tutti</option>
              <option value="nuova">Nuova</option>
              <option value="assegnata">Assegnata</option>
              <option value="schedulata">Schedulata</option>
              <option value="in_lavorazione">In Lavorazione</option>
              <option value="completata">Completata</option>
            </select>
          </div>
          <div class="ml-auto flex items-center gap-3 text-xs text-gray-500">
            <span v-for="(color, key) in PRIORITY_COLORS" :key="key" class="flex items-center gap-1">
              <span class="inline-block w-2.5 h-2.5 rounded-sm" :style="{ background: color }"></span>
              {{ key.charAt(0).toUpperCase() + key.slice(1) }}
            </span>
          </div>
          <Loader :visible="isLoading" text="Salvataggio..." />
        </div>

        <div class="flex-1 min-h-0">
          <BryntumSchedulerPro
            :key="schedulerKey"
            ref="schedulerPro"
            v-bind="schedulerConfig"
            style="height:100%"
            @eventDrop="onEventDrop"
            @eventResizeEnd="onEventResizeEnd"
          />
        </div>
      </div>
    </div>

    <div v-if="assignDialog.show" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
        <button @click="assignDialog.show = false" class="absolute top-3 right-3 text-gray-400 hover:text-gray-600">
          <i class="bi bi-x-lg"></i>
        </button>
        <h3 class="text-lg font-semibold mb-1">Assegna attività</h3>
        <p class="text-sm text-gray-500 mb-4">{{ assignDialog.activity?.title }}</p>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Assegna a</label>
            <select v-model="assignDialog.userId" class="w-full text-sm border-gray-300 rounded-md">
              <option value="">— Seleziona utente —</option>
              <option v-for="u in users" :key="u.id" :value="u.id">{{ u.name }}</option>
            </select>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Data inizio</label>
              <input v-model="assignDialog.startDate" type="date" class="w-full text-sm border-gray-300 rounded-md" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Data fine</label>
              <input v-model="assignDialog.endDate" type="date" class="w-full text-sm border-gray-300 rounded-md" />
            </div>
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-2">
          <button @click="assignDialog.show = false" class="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50">
            Annulla
          </button>
          <button
            @click="saveAssignment"
            :disabled="!assignDialog.userId || isSaving"
            class="px-4 py-1.5 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {{ isSaving ? 'Salvataggio...' : 'Assegna' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import Loader from '@/Components/Loader.vue';
import { BryntumSchedulerPro } from '@bryntum/schedulerpro-vue-3';

const PRIORITY_COLORS = {
  bassa:   '#22c55e',
  media:   '#3b82f6',
  alta:    '#f97316',
  critica: '#ef4444',
};

const STATUS_COLORS = {
  completata: '#9ca3af',
};

export default {
  props: ['resources', 'events', 'assignments', 'pool', 'users'],

  components: { Link, BreadCrumbs, Loader, BryntumSchedulerPro },

  data() {
    return {
      PRIORITY_COLORS,
      isLoading:    false,
      isSaving:     false,
      filterPriority: '',
      filterStatus:   '',
      poolSearch:     '',
      selectedPool:   null,
      poolData:       [...(this.pool || [])],
      allEvents:      [...(this.events || [])],
      allAssignments: [...(this.assignments || [])],
      assignDialog: {
        show:      false,
        activity:  null,
        userId:    '',
        startDate: '',
        endDate:   '',
      },
      navigation: [
        { name: 'Dashboard', href: route('dashboard') },
        { name: 'Attività', href: route('modules.home', 'attivita') },
        { name: 'Gantt', href: route('modules.attivita.gantt') },
      ],
      schedulerConfig: {},
      schedulerKey: 0,
    };
  },

  computed: {
    filteredPool() {
      const q = this.poolSearch.toLowerCase();
      return this.poolData.filter(a =>
        !q ||
        a.title.toLowerCase().includes(q) ||
        (a.company_name || '').toLowerCase().includes(q)
      );
    },
  },

  watch: {
    pool(newPool) {
      this.poolData = [...(newPool || [])];
    },
    events(newEvents) {
      this.allEvents = [...(newEvents || [])];
      this.rebuildScheduler();
    },
    assignments(newAssignments) {
      this.allAssignments = [...(newAssignments || [])];
    },
  },

  mounted() {
    this.schedulerConfig = this.buildConfig(this.allEvents, this.allAssignments);
  },

  methods: {
    reloadPool() {
      this.isLoading = true;
      router.reload({
        only: ['pool', 'events', 'assignments'],
        onFinish: () => { this.isLoading = false; },
      });
    },

    rebuildScheduler() {
      this.schedulerConfig = this.buildConfig(this.allEvents, this.allAssignments);
      this.schedulerKey++;
    },

    buildConfig(events, assignments) {
      const filteredEvents = events.filter(e => {
        if (this.filterPriority && e.priority !== this.filterPriority) return false;
        if (this.filterStatus   && e.status   !== this.filterStatus)   return false;
        return true;
      });

      const filteredEventIds = new Set(filteredEvents.map(e => e.id));
      const filteredAssignments = assignments.filter(a => filteredEventIds.has(a.event));

      const start = this.computeStartDate(filteredEvents);
      const end   = this.computeEndDate(filteredEvents);

      return {
        startDate:  start,
        endDate:    end,
        viewPreset: 'weekAndDay',
        rowHeight:  40,
        barMargin:  4,
        readOnly:   false,
        columns: [
          { text: 'Risorsa', field: 'name', width: 180, flex: 0 },
        ],
        project: {
          resourcesData:   this.resources || [],
          eventsData:      filteredEvents,
          assignmentsData: filteredAssignments,
          autoSetConstraints: false,
        },
        features: {
          cellEdit:    false,
          eventEdit:   false,
          eventDrag:   { constrainDragToTimeline: true },
          eventResize: true,
          scheduleMenu: false,
          eventMenu: {
            items: {
              unassign: {
                text: '<i class="bi bi-person-dash mr-1"></i>Rimuovi assegnazione',
                icon: '',
                onItem: ({ eventRecord, resourceRecord }) => {
                  this.removeAssignment(eventRecord.id, resourceRecord.id);
                },
              },
              editEvent: false,
              deleteEvent: false,
            },
          },
        },
        eventRenderer: ({ eventRecord, renderData }) => {
          const color = STATUS_COLORS[eventRecord.status]
            || PRIORITY_COLORS[eventRecord.priority]
            || '#6b7280';
          renderData.style = `background-color:${color};border-color:${color};`;
          return eventRecord.name;
        },
      };
    },

    applyFilters() {
      this.rebuildScheduler();
    },

    computeStartDate(events) {
      if (!events.length) return new Date();
      const dates = events.map(e => new Date(e.startDate)).filter(d => !isNaN(d));
      if (!dates.length) return new Date();
      const min = new Date(Math.min(...dates));
      min.setDate(min.getDate() - 7);
      return min;
    },

    computeEndDate(events) {
      const fallback = () => { const d = new Date(); d.setMonth(d.getMonth() + 3); return d; };
      if (!events.length) return fallback();
      const dates = events.map(e => new Date(e.endDate)).filter(d => !isNaN(d));
      if (!dates.length) return fallback();
      const max = new Date(Math.max(...dates));
      max.setDate(max.getDate() + 14);
      return max;
    },

    openAssignDialog(act) {
      this.assignDialog = {
        show:      true,
        activity:  act,
        userId:    '',
        startDate: act.start_date || '',
        endDate:   act.end_date   || '',
      };
    },

    async saveAssignment() {
      this.isSaving = true;
      try {
        await axios.post(route('modules.attivita.assegna'), {
          activity_id: this.assignDialog.activity.id,
          user_id:     this.assignDialog.userId,
          start_date:  this.assignDialog.startDate || null,
          end_date:    this.assignDialog.endDate   || null,
        });

        const act = this.assignDialog.activity;
        const user = this.users.find(u => u.id === this.assignDialog.userId);

        this.poolData = this.poolData.filter(a => a.id !== act.id);
        this.selectedPool = null;

        const newEvent = {
          id:                act.id,
          name:              act.title,
          startDate:         this.assignDialog.startDate || act.start_date || act.end_date,
          endDate:           this.assignDialog.endDate   || act.end_date,
          manuallyScheduled: true,
          status:            act.status,
          priority:          act.priority,
          company_name:      act.company_name,
          order_id:          act.order_id,
        };

        if (!this.allEvents.find(e => e.id === act.id)) {
          this.allEvents.push(newEvent);
        }

        const nextId = (this.allAssignments.length ? Math.max(...this.allAssignments.map(a => a.id)) : 0) + 1;
        this.allAssignments.push({ id: nextId, event: act.id, resource: this.assignDialog.userId });

        this.rebuildScheduler();
        this.assignDialog.show = false;
      } catch (e) {
        console.error('Errore assegnazione:', e);
      } finally {
        this.isSaving = false;
      }
    },

    async removeAssignment(activityId, userId) {
      this.isLoading = true;
      try {
        await axios.delete(route('modules.attivita.deassegna', activityId), {
          data: { user_id: userId },
        });

        this.allAssignments = this.allAssignments.filter(
          a => !(a.event === activityId && a.resource === userId)
        );

        const stillAssigned = this.allAssignments.some(a => a.event === activityId);
        if (!stillAssigned) {
          const ev = this.allEvents.find(e => e.id === activityId);
          if (ev) {
            this.allEvents = this.allEvents.filter(e => e.id !== activityId);
            this.poolData.push({
              id:           ev.id,
              title:        ev.name,
              status:       ev.status,
              priority:     ev.priority,
              company_name: ev.company_name,
              order_id:     ev.order_id,
              start_date:   ev.startDate,
              end_date:     ev.endDate,
            });
          }
        }

        this.rebuildScheduler();
      } catch (e) {
        console.error('Errore rimozione assegnazione:', e);
      } finally {
        this.isLoading = false;
      }
    },

    async onEventDrop({ eventRecords, context }) {
      for (const eventRecord of eventRecords) {
        const newResource = context?.newResource || context?.resourceRecord;
        const oldResource = context?.origAssignments?.[0]?.resource;

        if (newResource && oldResource && newResource.id !== oldResource.id) {
          this.isLoading = true;
          try {
            await axios.patch(route('modules.attivita.sposta-assegnazione', eventRecord.id), {
              old_user_id: oldResource.id,
              new_user_id: newResource.id,
              start_date:  eventRecord.startDate instanceof Date ? eventRecord.startDate.toISOString().split('T')[0] : String(eventRecord.startDate).split('T')[0],
              end_date:    eventRecord.endDate   instanceof Date ? eventRecord.endDate.toISOString().split('T')[0]   : String(eventRecord.endDate).split('T')[0],
            });
            const idx = this.allAssignments.findIndex(a => a.event === eventRecord.id && a.resource === oldResource.id);
            if (idx >= 0) this.allAssignments[idx].resource = newResource.id;
          } catch (e) {
            console.error('Errore spostamento assegnazione:', e);
          } finally {
            this.isLoading = false;
          }
        } else {
          await this.persistDates(eventRecord);
        }
      }
    },

    async onEventResizeEnd({ eventRecord }) {
      await this.persistDates(eventRecord);
    },

    async persistDates(eventRecord) {
      const startDate = eventRecord.startDate instanceof Date
        ? eventRecord.startDate.toISOString().split('T')[0]
        : String(eventRecord.startDate).split('T')[0];
      const endDate = eventRecord.endDate instanceof Date
        ? eventRecord.endDate.toISOString().split('T')[0]
        : String(eventRecord.endDate).split('T')[0];

      this.isLoading = true;
      try {
        await axios.patch(route('modules.attivita.aggiorna-date', eventRecord.id), {
          start_date: startDate,
          end_date:   endDate,
        });
        const ev = this.allEvents.find(e => e.id === eventRecord.id);
        if (ev) { ev.startDate = startDate; ev.endDate = endDate; }
      } catch (e) {
        console.error('Errore salvataggio date:', e);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style>
@import "@bryntum/schedulerpro/schedulerpro.css";
@import "@bryntum/schedulerpro/material3-light.css";

.b-grid-row.b-selected .b-grid-cell {
  background-color: #eff6ff;
}
</style>
